import React, { useMemo, useState } from "react";
import {
  Navbar,
  Nav,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Card,
  Accordion,
  Form,
  Alert,
} from "react-bootstrap";
import {
  Truck,
  Tools,
  ShieldCheck,
  Telephone,
  StarFill,
  CheckCircle,
  LightningCharge,
  Whatsapp,
} from "react-bootstrap-icons";

const BRAND = "EasyLaundryRental.com";
const PRICE = 60;

type Feature = {
  icon: React.ReactNode;
  title: string;
  text: string;
};

const FEATURES: Feature[] = [
  {
    icon: <Truck size={28} className="me-2" />,
    title: "Free Delivery & Installation",
    text: "We handle the heavy lifting and hookups—no hidden fees, ever.",
  },
  {
    icon: <Tools size={28} className="me-2" />,
    title: "Fast Service",
    text: "If something breaks, we fix or replace it—typically within 24–48 hours.",
  },
  {
    icon: <ShieldCheck size={28} className="me-2" />,
    title: "No Long Contracts",
    text: "Month-to-month flexibility with easy upgrades when you need them.",
  },
  {
    icon: <LightningCharge size={28} className="me-2" />,
    title: "Energy Efficient",
    text: "Modern, efficient machines that help keep utility bills in check.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Installers were in and out in under 30 minutes and the machines run perfectly.",
    name: "Maya T.",
    detail: "Apartment Renter",
    stars: 5,
  },
  {
    quote:
      "As a property manager, this is a no-brainer—tenants love it and it saves me headaches.",
    name: "Chris R.",
    detail: "Property Manager",
    stars: 5,
  },
  {
    quote:
      "College move-in was crazy, but laundry wasn’t. Affordable and super convenient.",
    name: "Jordan K.",
    detail: "Student",
    stars: 5,
  },
];

const FAQS = [
  {
    q: "How much does it cost?",
    a: `It's a flat $${PRICE}/month for a washer & dryer set. Delivery and installation are free.`,
  },
  {
    q: "Is there a long-term commitment?",
    a: "Nope. It’s month-to-month. Cancel anytime with a simple pick-up appointment.",
  },
  {
    q: "What if a machine stops working?",
    a: "Message us and we’ll repair or replace quickly—at no extra cost.",
  },
  {
    q: "Do you install in apartments?",
    a: "Yes. We work with apartments, condos, single-family homes, and student housing.",
  },
];

const SectionHeading: React.FC<{ kicker?: string; title: string; sub?: string }> =
  ({ kicker, title, sub }) => (
    <div className="text-center mb-4">
      {kicker && (
        <Badge bg="secondary" className="text-uppercase rounded-pill mb-2">
          {kicker}
        </Badge>
      )}
      <h2 className="fw-bold">{title}</h2>
      {sub && <p className="text-muted lead mb-0">{sub}</p>}
    </div>
  );

const FeatureCard: React.FC<Feature> = ({ icon, title, text }) => (
  <Card className="h-100 shadow-sm border-0">
    <Card.Body>
      <div className="d-flex align-items-start">
        <div className="text-primary">{icon}</div>
        <div>
          <Card.Title className="mb-1">{title}</Card.Title>
          <Card.Text className="text-muted mb-0">{text}</Card.Text>
        </div>
      </div>
    </Card.Body>
  </Card>
);

const Stars: React.FC<{ n: number }> = ({ n }) => (
  <>
    {Array.from({ length: n }).map((_, i) => (
      <StarFill key={i} className="text-warning me-1" />
    ))}
  </>
);

const TestimonialCard: React.FC<{
  quote: string;
  name: string;
  detail: string;
  stars: number;
}> = ({ quote, name, detail, stars }) => (
  <Card className="h-100 shadow-sm border-0">
    <Card.Body>
      <div className="mb-2">
        <Stars n={stars} />
      </div>
      <blockquote className="mb-3 fst-italic">“{quote}”</blockquote>
      <div className="small text-muted">
        <span className="fw-semibold">{name}</span> • {detail}
      </div>
    </Card.Body>
  </Card>
);

const PricingCard: React.FC = () => (
  <Card className="shadow-lg border-0 overflow-hidden">
    <Card.Body className="p-4">
      <Badge bg="success" className="mb-2">
        Most Popular
      </Badge>
      <h3 className="fw-bold mb-1">${PRICE}/month</h3>
      <div className="text-muted mb-3">Washer + Dryer Set</div>
      <ul className="list-unstyled mb-4">
        {[
          "Free delivery & professional installation",
          "Month-to-month—no long contract",
          "Repair/replace guarantee",
          "Energy-efficient models",
        ].map((item) => (
          <li key={item} className="mb-2 d-flex align-items-start">
            <CheckCircle className="text-success me-2 mt-1" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <div className="d-grid gap-2">
        <Button href="#lead" size="lg">
          Reserve My Set
        </Button>
        <Button variant="outline-secondary" href="tel:+1-000-000-0000">
          <Telephone className="me-2" />
          Call to Ask a Question
        </Button>
      </div>
    </Card.Body>
    <Card.Footer className="bg-light text-center">
      <small className="text-muted">
        Pricing includes standard hookups. Custom installs quoted on request.
      </small>
    </Card.Footer>
  </Card>
);

const FAQ: React.FC = () => (
  <Accordion alwaysOpen>
    {FAQS.map((f, i) => (
      <Accordion.Item eventKey={String(i)} key={i}>
        <Accordion.Header>{f.q}</Accordion.Header>
        <Accordion.Body>{f.a}</Accordion.Body>
      </Accordion.Item>
    ))}
  </Accordion>
);

const LeadForm: React.FC = () => {
  const [validated, setValidated] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    const form = e.currentTarget;
    e.preventDefault();
    if (!form.checkValidity()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    setLoading(true);
    // Simulate async submit
    setTimeout(() => {
      setSubmitted(true);
      setLoading(false);
    }, 800);
  };

  if (submitted) {
    return (
      <Alert variant="success" className="shadow-sm">
        Thanks! We’ll text or email you shortly to schedule delivery.
      </Alert>
    );
  }

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      <Row className="g-3">
        <Col md={6}>
          <Form.Group controlId="firstName">
            <Form.Label>First Name</Form.Label>
            <Form.Control required placeholder="Jane" />
            <Form.Control.Feedback type="invalid">
              Please enter your first name.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="lastName">
            <Form.Label>Last Name</Form.Label>
            <Form.Control required placeholder="Doe" />
            <Form.Control.Feedback type="invalid">
              Please enter your last name.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={7}>
          <Form.Group controlId="email">
            <Form.Label>Email</Form.Label>
            <Form.Control type="email" required placeholder="you@example.com" />
            <Form.Control.Feedback type="invalid">
              Please provide a valid email.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={5}>
          <Form.Group controlId="phone">
            <Form.Label>Mobile (for scheduling)</Form.Label>
            <Form.Control
              type="tel"
              required
              placeholder="(555) 555-5555"
              pattern="^[0-9()\-\s+.]{10,}$"
            />
            <Form.Control.Feedback type="invalid">
              Please provide a phone number.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group controlId="address1">
            <Form.Label>Installation Address</Form.Label>
            <Form.Control required placeholder="123 Main St, Unit 4" />
            <Form.Control.Feedback type="invalid">
              Please provide an address.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="residenceType">
            <Form.Label>Residence Type</Form.Label>
            <Form.Select required defaultValue="">
              <option value="" disabled>
                Choose…
              </option>
              <option>Apartment</option>
              <option>Condo</option>
              <option>Single-Family Home</option>
              <option>Student Housing</option>
              <option>Other</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              Please select a residence type.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="moveIn">
            <Form.Label>Target Install Date</Form.Label>
            <Form.Control type="date" required />
            <Form.Control.Feedback type="invalid">
              Please choose an install date.
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col xs={12} className="pt-2">
          <Form.Check
            required
            id="tos"
            label={
              <>
                I agree to be contacted about scheduling and understand the{" "}
                <a href="#terms">Terms</a>.
              </>
            }
          />
        </Col>
        <Col xs={12} className="d-grid pt-2">
          <Button type="submit" size="lg" disabled={loading}>
            {loading ? "Submitting…" : "Get My $60/mo Set"}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const AppEasyLaundryRental: React.FC = () => {  
  const year = useMemo(() => new Date().getFullYear(), []);
  console.log("easy laundry rental");
  return (
    <>
      {/* Sticky Nav */}
      <Navbar bg="light" expand="lg" fixed="top" className="border-bottom">
        <Container>
          <Navbar.Brand className="fw-bold">{BRAND}</Navbar.Brand>
          <Navbar.Toggle aria-controls="nav" />
          <Navbar.Collapse id="nav">
            <Nav className="ms-auto">
              <Nav.Link href="#features">Features</Nav.Link>
              <Nav.Link href="#pricing">Pricing</Nav.Link>
              <Nav.Link href="#faq">FAQ</Nav.Link>
              <Button href="#lead" className="ms-lg-3">
                Reserve Now
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero */}
      <section className="pt-6" style={{ paddingTop: "88px" }}>
        <Container className="py-5">
          <Row className="align-items-center g-4">
            <Col lg={6}>
              <Badge bg="primary" className="mb-3">
                Washer & Dryer Rentals
              </Badge>
              <h1 className="display-5 fw-bold">
                Laundry made easy—just ${PRICE}/month.
              </h1>
              <p className="lead text-muted">
                Cozy, hassle-free laundry at home. Free delivery and professional
                installation—perfect for apartments, homes, and student housing.
              </p>
              <div className="d-flex gap-2 flex-wrap">
                <Button size="lg" href="#lead">
                  Get Started
                </Button>
                <Button
                  variant="outline-secondary"
                  size="lg"
                  href="tel:+1-000-000-0000"
                >
                  <Telephone className="me-2" />
                  Call Us
                </Button>
                <Button
                  variant="outline-success"
                  size="lg"
                  href="https://wa.me/10000000000"
                >
                  <Whatsapp className="me-2" />
                  Chat
                </Button>
              </div>
              <div className="mt-3 small text-muted">
                No long-term contracts • Repair/replace guarantee
              </div>
            </Col>
            <Col lg={6}>
              <Card className="shadow border-0">
                <Card.Img
                  alt="Washer and dryer set"
                  src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=1200&auto=format&fit=crop"
                />
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <CheckCircle className="text-success me-2" />
                    <div>
                      <strong>Free delivery & install</strong>
                      <div className="text-muted small">
                        Standard hookups included
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features */}
      <section id="features" className="py-5 bg-light">
        <Container>
          <SectionHeading
            kicker="Why Choose Us"
            title="Comfort, convenience, and real support"
            sub="Everything you need to keep life moving—without laundry-day drama."
          />
          <Row className="g-4">
            {FEATURES.map((f) => (
              <Col md={6} lg={3} key={f.title}>
                <FeatureCard {...f} />
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-5">
        <Container>
          <SectionHeading
            kicker="Simple Pricing"
            title={`Just $${PRICE}/month`}
            sub="No hidden fees. Cancel anytime."
          />
          <Row className="justify-content-center">
            <Col md={10} lg={7}>
              <PricingCard />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Social Proof */}
      <section className="py-5 bg-light">
        <Container>
          <SectionHeading
            kicker="Trusted By Renters & Managers"
            title="What our customers say"
          />
          <Row className="g-4">
            {TESTIMONIALS.map((t, idx) => (
              <Col md={6} lg={4} key={idx}>
                <TestimonialCard {...t} />
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Lead Capture */}
      <section id="lead" className="py-5">
        <Container>
          <Row className="g-5 align-items-start">
            <Col lg={6}>
              <SectionHeading
                kicker="Reserve Today"
                title="Tell us where to deliver"
                sub="We’ll confirm availability and schedule a convenient install time."
              />
              <LeadForm />
              <div id="terms" className="small text-muted mt-3">
                By submitting, you agree to be contacted about delivery and
                installation. Standard message/data rates may apply.
              </div>
            </Col>
            <Col lg={6}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <h5 className="fw-bold mb-3">What to expect</h5>
                  <ol className="mb-0 text-muted">
                    <li className="mb-2">
                      We confirm your details and installation window.
                    </li>
                    <li className="mb-2">
                      Our team delivers, installs, and tests your machines.
                    </li>
                    <li className="mb-2">
                      Enjoy laundry at home. Need help? We’re a quick call away.
                    </li>
                  </ol>
                </Card.Body>
              </Card>
              <Card className="border-0 shadow-sm mt-3">
                <Card.Body className="d-flex align-items-center">
                  <Telephone className="me-3" size={22} />
                  <div>
                    <div className="fw-semibold">Questions?</div>
                    <a href="tel:+1-000-000-0000" className="text-decoration-none">
                      +1 (000) 000-0000
                    </a>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5 bg-light">
        <Container>
          <SectionHeading kicker="FAQ" title="Quick answers" />
          <Row className="justify-content-center">
            <Col md={10} lg={8}>
              <FAQ />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4 border-top bg-white">
        <Container className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          <div className="small text-muted">
            © {year} {BRAND}. All rights reserved.
          </div>
          <div className="d-flex gap-3 small">
            <a href="#" className="text-decoration-none">
              Privacy
            </a>
            <a href="#" className="text-decoration-none">
              Terms
            </a>
            <a href="#lead" className="text-decoration-none">
              Get Started
            </a>
          </div>
        </Container>
      </footer>
    </>
  );
};

export default AppEasyLaundryRental;
